import { types, getParent, flow } from 'mobx-state-tree';
import { user, stats } from "@myplay/all";

const clubsModule = user.clubs;
const teamsModule = user.teams;
const statsModule = stats.stats;

const Team = types
  .model(`Team`, {
    _id: types.maybeNull(types.string),
    name: types.maybeNull(types.string),
    code: types.maybeNull(types.string),
    teamImage: types.maybeNull(types.string),
    category: types.frozen({}),
    members: types.frozen({}),
    videosCount: types.maybeNull(types.number),
    highlightsCount: types.maybeNull(types.number),
    year: types.maybeNull(types.number), 
    site: types.frozen({}), 
    stats: types.maybeNull(types.frozen({})), 
    videos: types.maybeNull(types.array(types.frozen({}))), 
    highlights: types.maybeNull(types.array(types.frozen({})))
  });

const AllTeamsInfo = types
  .model("AllTeamsInfo", {
    highlightsCount: types.number,
    videosCount: types.number,
    membersCount: types.number     
  });

const TeamsStore = types
  .model("Teams", {
    teams: types.array(types.frozen(Team)),
    selectedTeam: types.maybeNull(Team),
    allTeamsInfo: types.maybeNull(AllTeamsInfo)
  })
  .actions(self => ({
    setTeams(teams) {
      self.teams = [ ...teams ];
      teams.map(team => {
        self.getTeamRecentVideos(team._id);
        self.getTeamHighlights(team._id);
      });
      self.setAllTeamsInfo();
      self.getTeamsStats(self.teams); 
    },
    setSelectedTeam(teamId) {
      if (!teamId) {
        self.selectedTeam = null;
        return;
      };
      const { SitesStore } = getParent(self);
      const selectedTeam = self.teams.filter(team => team._id === teamId)[0];
      const { connectedSiteId } = selectedTeam;
      const selectedTeamsSite = connectedSiteId ? SitesStore.getSiteById(connectedSiteId) : null;
      self.selectedTeam = { ...selectedTeam, site: { ...selectedTeamsSite || null } }
    }, 
    getTeamNameById(teamId) {
      if (teamId) {
        const team = self.teams.filter(team => team._id === teamId)[0];
        return team ? team.name : "";
      };
    },
    createNewTeam: flow(function* createNewTeam(team) {
      try {
        const { ClubsStore: { currentClub, getClubTeams } } = getParent(self);
        yield clubsModule.createNewTeam(currentClub.club._id, team);
        yield getClubTeams();
      } catch (error) {
        console.log(error);
      }
    }),
    getAllClubPlayers() {
      let allPlayers = [];
      self.teams.map(team => {
          team.members.players.map(player => {
            const indexOfPlayer = allPlayers.findIndex(currentPlayer => currentPlayer._id === player._id)
            if (indexOfPlayer === -1) {
              allPlayers.push(player);
            }
          });
      });
      return allPlayers;
    }, 
    attachUsersToTeam: flow(function* attachUsersToTeam(usersIds){
      try {
        yield teamsModule.attachUsersToTeam(self.selectedTeam._id, usersIds);
        self.updateTeamMembers(self.selectedTeam._id);
      } catch (error) {
        console.log(error);
      }
    }), 
    getTeamsStats: flow(function* getTeamsStats(teams) {
      try {
        teams.map(async (team, i) => {
          const teamStats = await statsModule.getTeamSeasonStats(team._id);
          self.setTeamStats(teamStats, i);
        })
      } catch (error) {
        console.log(error);
      }
    }),
    setTeamStats(stats, index) {
      self.teams[index] = { 
        ...self.teams[index], 
        stats 
      };
    },
    getTeamIdByName(name) {
      return self.teams.filter(team => team.name === name)[0]._id;
    },
    setAllTeamsInfo() {
      const { teams } = self;
      const allTeamsInfo = {
        highlightsCount: 0,
        membersCount: 0,
        videosCount: 0    
      }
      const uniquTeamMembersIds = [];

      teams.map(team => {
        allTeamsInfo.highlightsCount += team.highlightsCount;
        allTeamsInfo.videosCount += team.videosCount;
        
        team.members.players.map(member => {
          if (!uniquTeamMembersIds.includes(member._id)) {
            uniquTeamMembersIds.push(member._id);
          };
        });
      });

      allTeamsInfo.membersCount = uniquTeamMembersIds.length;
      self.allTeamsInfo = { ...allTeamsInfo }
    }, 
    getTeamRecentVideos: flow(function* getTeamRecentVideos(teamId, limit = 4, skip = 0, shouldOverrideVideos = true){
      try {
        const videos = yield teamsModule.getTeamVideos(teamId, limit, skip);
        self.setTeamRecentVideos(videos,teamId, shouldOverrideVideos);
      } catch (error) {
        console.log(error);
      }
    }), 
    setTeamRecentVideos(videos, teamId, shouldOverrideVideos) {
      const currentTeamIndex = self.teams.findIndex(team => team._id === teamId);
      const newVideos = !self.teams[currentTeamIndex].videos || shouldOverrideVideos ? 
        [...videos] :
        [...self.teams[currentTeamIndex].videos, ...videos];
      const newTeam  = {...self.teams[currentTeamIndex], videos: newVideos };
      self.teams[currentTeamIndex] = { ...newTeam };

      if (self.selectedTeam && self.teams[currentTeamIndex]._id === self.selectedTeam._id) {
        self.selectedTeam = { ...newTeam }
      }
    }, 
    removePlayerFromTeam: flow(function* removePlayerFromTeam(player, team){
      try {
        yield teamsModule.removeUserFromTeam(player._id, team._id);
        self.removePlayerFromTeamStore(player._id, team._id);
      } catch (error) {
        console.log(error);
      }
    }), 
    removePlayerFromTeamStore(playerId, teamId) {
      const teamIndex = self.teams.findIndex(team => team._id === teamId);
      const newTeamPlayers = self.teams[teamIndex].members.players.filter(player => player._id !== playerId);
      let newTeam = {...self.teams[teamIndex]};
      newTeam.members = {
        ...newTeam.members, 
        players: [...newTeamPlayers]
      };

      if (self.selectedTeam) {
        self.selectedTeam.members = { ...newTeam.members };
      };

      self.teams[teamIndex] = { ...newTeam };
    }, 
    updateTeamDetails: flow(function* updateTeamDetails(newTeam){
      try {
        const { CategoriesStore } = getParent(self);
        const category = CategoriesStore.getCategoryByName(newTeam.category);
        const params = {
          ...newTeam, 
          category
        };
        const team = yield teamsModule.updateTeam(self.selectedTeam._id, params);
        const stats = yield statsModule.getTeamSeasonStats(team._id);
        const teamIndex = self.teams.findIndex(currentTeam => currentTeam._id === self.selectedTeam._id);

        self.selectedTeam = {
          ...team,
          members: {...self.selectedTeam.members},
          videos: [...self.selectedTeam.videos], 
          stats, 
          category 
        };

        self.teams[teamIndex] = { ...self.selectedTeam };
        self.setTeamStats(stats, teamIndex);
        self.setTeamCategory(category, teamIndex);

      } catch (error) {
        console.log(error);
      }
    }),
    getTeamById(teamId) {
      return self.teams.filter(team => team._id === teamId)[0];
    }, 
    getNotRelatedPlayers() {
      const allClubPlayers = self.getAllClubPlayers();
      const teamPlayers = self.selectedTeam.members.players;
      const notRelatedPlayers = allClubPlayers.filter(player => teamPlayers.findIndex(currentPlayer => currentPlayer._id === player._id) === -1);
      return notRelatedPlayers;
    }, 
    updateTeamMembers: flow(function* updateTeamMembers(teamId) {
      try {
        const teamIndex = self.teams.findIndex(team => team._id === teamId);
        const members = yield teamsModule.getTeamMembers(self.selectedTeam._id);
        let newTeam = {...self.teams[teamIndex]};
        newTeam.members = { ...members };
        self.teams[teamIndex] = { ...newTeam }
        self.selectedTeam.members = { ...members };
      } catch (error) {
        console.log(error);
      }
    }), 
    setTeamCategory(category, index) {
      self.teams[index] = { 
        ...self.teams[index],
        category 
      }
    }, 
    removeTeamFromClub: flow(function* removeTeamFromClub(teamId){
      try {
        const { ClubsStore: { currentClub: { club: { _id } } , getClubById } } = getParent(self);
        yield clubsModule.removeTeam(_id, teamId);
        yield getClubById(_id);
      } catch (error) {
        console.log(error);
      }
    }), 
    refreshTeamCode: flow(function* refreshTeamCode(teamId){
      try {
        const { code } = yield teamsModule.refreshTeamCode(teamId);
        const currentTeamIndex = self.teams.findIndex(team => team._id === teamId);
        self.selectedTeam.code = code;
        const newTeam = {...self.teams[currentTeamIndex], code}
        self.teams[currentTeamIndex] = {...newTeam};

      } catch (error) {
        console.log(error);
      }
    }), 
    getPlayerStats: flow(function* getPlayerStats(playerId, teamId){
      try {
        const stats = yield statsModule.getPlayerProfileStats(playerId, teamId);
        return stats;
      } catch (error) {
        console.log(error);
      }
    }), 
    getTeamHighlights: flow(function* getTeamHighlights(teamId, limit = 4, skip = 0){
      try {
        const { highlights } = yield teamsModule.getTeamHighlights(teamId, limit, skip);
        self.setTeamRecentHighlights(highlights, teamId);
      } catch (error) {
        console.log(error);
      }
    }), 
    setTeamRecentHighlights(highlights, teamId) {
      const currentTeamIndex = self.teams.findIndex(team => team._id === teamId);
      const newHighlights = self.teams[currentTeamIndex].highlights ? 
        [...self.teams[currentTeamIndex].highlights, ...highlights] : [...highlights]; 
      const newTeam  = {...self.teams[currentTeamIndex], highlights: newHighlights };
      self.teams[currentTeamIndex] = { ...newTeam };

      if (self.selectedTeam && self.teams[currentTeamIndex]._id === self.selectedTeam._id) {
        self.selectedTeam = { ...newTeam }
      }
    }
  }))

export default TeamsStore;
